import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";

/**
 * Checks if any DB-contacts (category: "contact") exist.
 * Cached once confirmed true — re-checks if false/null (e.g. after import).
 */
export const checkContactsExist = createAsyncThunk(
  "setupGuide/checkContactsExist",
  async (_, { getState }) => {
    const { setupGuide } = getState();
    if (setupGuide.contactsExist === true) return { contactsExist: true };

    try {
      const res = await api.post("/getAllContactsOrLeads", {
        isLead: false,
        page: 1,
        limit: 1,
        category: "contact",
      });
      const total = res.data?.pagination?.totalItems ?? 0;
      return { contactsExist: total > 0 };
    } catch {
      return { contactsExist: false };
    }
  }
);

/**
 * Checks if any leads (category: "lead") exist.
 * Cached once confirmed true — re-checks if false/null (e.g. after import).
 */
export const checkLeadsExist = createAsyncThunk(
  "setupGuide/checkLeadsExist",
  async (_, { getState }) => {
    const { setupGuide } = getState();
    if (setupGuide.leadsExist === true) return { leadsExist: true };

    try {
      const res = await api.post("/getAllContactsOrLeads", {
        isLead: true,
        page: 1,
        limit: 1,
        category: "lead",
      });
      const total = res.data?.pagination?.totalItems ?? 0;
      return { leadsExist: total > 0 };
    } catch {
      return { leadsExist: false };
    }
  }
);

const setupGuideSlice = createSlice({
  name: "setupGuide",
  initialState: {
    contactsExist: null, // null = not yet checked
    leadsExist: null,    // null = not yet checked
    checking: false,
  },
  reducers: {
    // Call after a successful DB import to force a re-check
    invalidateContactsCache(state) {
      state.contactsExist = null;
    },
    // Call after a successful leads import to force a re-check
    invalidateLeadsCache(state) {
      state.leadsExist = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkContactsExist.pending, (state) => { state.checking = true; })
      .addCase(checkContactsExist.fulfilled, (state, action) => {
        state.contactsExist = action.payload.contactsExist;
        state.checking = false;
      })
      .addCase(checkContactsExist.rejected, (state) => { state.checking = false; })

      .addCase(checkLeadsExist.pending, (state) => { state.checking = true; })
      .addCase(checkLeadsExist.fulfilled, (state, action) => {
        state.leadsExist = action.payload.leadsExist;
        state.checking = false;
      })
      .addCase(checkLeadsExist.rejected, (state) => { state.checking = false; });
  },
});

export const { invalidateContactsCache, invalidateLeadsCache } = setupGuideSlice.actions;
export default setupGuideSlice.reducer;
